import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactElement, type ReactNode, type RefObject } from "react";
import { flushSync } from "react-dom";
import { CREATOR_ROWS, creatorRow, reduceCreator, type Appearance, type CreatorOffer, type CreatorOption, type CreatorRow } from "odyssey-shared";
import { Button, Segmented, choiceForKey, logical, useScrollCue, type GlyphName } from "../../ui/kit/index.ts";
import { classes } from "../../ui/kit/classes.ts";
import { cssPerLogical } from "../../ui/kit/measure.ts";
import { LOOK_BODY_DEFAULT, LOOK_PANEL, previewColumn } from "./layout.ts";
import { LookFigure, type LookPreviewSource } from "./look-figure.tsx";
import { previewStage } from "./plan.ts";
import { swatchColour } from "./swatch-colours.ts";
import { lookCount, lookSwatch } from "./wording.ts";
import { say } from "../../words.ts";

/**
 * The Look panel (ADR-094 decisions 24, 27 and 28, its amendment A9, and B12 of the amendment "a temporary Change
 * look screen in the game"; the design card `look-step`, a visual reference): the preview at the left and, at the
 * right, the rows that choose a look over the creator's pure state (`creatorOffer`, `creatorRow`, `reduceCreator`),
 * under a note line, then whatever the mount draws after them (the Voice choice), and a refusal's sentence, with a
 * footer of Back, Randomise and the primary action. One component for
 * every place a look is chosen: whoever mounts it gives the window it is drawn in (`frame`), its title and its
 * footer's words, which rows show, the look and what to do with a change, whether it is saving, and the note. It
 * sends nothing and reads no session.
 *
 * Keyboard (decision 28, and the card's focus order): Tab reaches Turn left, Turn right, each row once, then the
 * footer's buttons; the left and right arrow keys turn the figure on a turn button and move a row's choice on a row;
 * Enter on a row is the primary action; Escape is the frame's. A row whose style has no colours (the hair that is
 * None) is not drawn, so Tab passes it.
 *
 * The preview column and its figure are sized from the window body's measure. A window takes its own size from the
 * page only as it is laid out, after the panel inside it, so the body is measured in the frame's resize step, the
 * panel is drawn again before that frame is painted, and until there is a measure the body is fully transparent:
 * the first thing a player sees is the settled layout, on a small page as on the card's. It is transparent and not
 * hidden, so the window's first focus still lands on its first control.
 */

/**
 * The words whoever mounts the panel gives it: the window's title and the footer's three buttons, with the word the
 * primary button shows while saving.
 */
export interface LookPanelWords {
  readonly title: string;
  readonly back: string;
  readonly randomise: string;
  readonly primary: string;
  readonly saving: string;
}

/**
 * What the panel hands the window it is drawn in.
 */
export interface LookFrameParts {
  readonly title: string;
  readonly footer: ReactNode;
  readonly children: ReactNode;
}

/**
 * A refusal the panel shows: its sentence, and the row it is about when it names one, under which it is drawn.
 */
export interface LookRefusal {
  readonly sentence: string;
  readonly row: CreatorRow | null;
}

/**
 * The panel's props.
 */
export interface LookPanelProps {
  /** Draws the window: the kit's `ModalWindow` over the game, an entry screen's `Window` at creation. */
  readonly frame: (parts: LookFrameParts) => ReactElement;
  readonly words: LookPanelWords;
  /** The rows shown, of `CREATOR_ROWS`, drawn in that order whatever order they are given in. */
  readonly rows: readonly CreatorRow[];
  readonly offer: CreatorOffer;
  /** The look shown, which must be one the offer holds (`creatorStart` makes one). */
  readonly look: Appearance;
  /** Told the look after each change. */
  readonly onLook: (look: Appearance) => void;
  /** What the preview draws from, or null on a page whose look sheets did not load. */
  readonly source: LookPreviewSource | null;
  /** Whether a request is out: every footer button is disabled and the primary one shows busy. */
  readonly saving: boolean;
  /** Whether the primary action is unavailable, as while the look is the one the character already has. */
  readonly primaryDisabled?: boolean;
  /** A line over the rows, under the window's title, in the kit's note style. */
  readonly note?: string | null;
  /**
   * Drawn after the look's rows, in the same scrolling column: the Voice choice at creation and in Change look
   * (ADR-094 notes 125 and 126). Its own controls take the keyboard as they do anywhere.
   */
  readonly after?: ReactNode;
  readonly refusal?: LookRefusal | null;
  /** Whether swatches and turn buttons are the touch size. */
  readonly touch?: boolean;
  /** The glyph the footer's first button leads with: the creation's Back has the chevron, Change look's Cancel none. */
  readonly backGlyph?: GlyphName;
  readonly onBack: () => void;
  readonly onPrimary: () => void;
  /** Draws Randomise's seed, an unsigned 32-bit whole number; the page's `crypto` unless given. */
  readonly seed?: () => number;
}

/**
 * A seed from the page's `crypto` (ADR-094 note 63): Randomise's, and the look a Look step or Change look opens on
 * when the creator picks it.
 *
 * @returns An unsigned 32-bit whole number.
 */
export function pageSeed(): number {
  return (crypto.getRandomValues(new Uint32Array(1))[0] as number) >>> 0;
}

/**
 * The window body's content size in logical pixels, taken each time the body is given a size, the first included,
 * and put in force before the frame it was taken in is painted.
 *
 * @param host The panel's element, which fills the window's body.
 * @returns The size, fractions kept; null until the first measure, outside the overlay (no `--px`), while the
 *   element has no size, and under `react-dom/server`.
 */
function useBodySize(host: RefObject<HTMLDivElement | null>): { readonly width: number; readonly height: number } | null {
  const [size, setSize] = useState<{ readonly width: number; readonly height: number } | null>(null);
  useLayoutEffect(() => {
    const element = host.current;
    if (element === null) {
      return undefined;
    }

    /**
     * Takes the element's size, or none while it cannot be measured, and draws the panel with it at once.
     */
    const measure = (): void => {
      const px = cssPerLogical(element);
      const rect = element.getBoundingClientRect();
      const next = px === null || rect.width <= 0 || rect.height <= 0 ? null : { width: rect.width / px, height: rect.height / px };
      flushSync(() => setSize((current) => (current?.width === next?.width && current?.height === next?.height ? current : next)));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [host]);
  return size;
}

/**
 * A row of swatches: a radio group whose one tab stop is the chosen swatch. A click picks a swatch; the arrow keys,
 * Home and End move the choice and wrap, as the kit's segmented choice does.
 *
 * @param props The group's `label`; its `options` in order; the chosen `index`; the swatch's `size` and the `gap`
 *   between swatches in logical pixels; and `onChoose` with the place picked.
 * @returns The radio group.
 */
function SwatchRow({ label, options, index, size, gap, onChoose }: { readonly label: string; readonly options: readonly CreatorOption[]; readonly index: number; readonly size: number; readonly gap: number; readonly onChoose: (index: number) => void }): ReactElement {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = options.map(() => false);

  /**
   * Moves the choice with the arrow keys, Home and End, and focuses the swatch chosen.
   *
   * @param event The key press on a swatch.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const from = buttons.current.findIndex((button) => button === event.target);
    const to = choiceForKey(event.key, enabled, from);
    if (to === null || to < 0) {
      return;
    }
    event.preventDefault();
    onChoose(to);
    buttons.current[to]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className="ot-look__swatches" data-look="swatches" onKeyDown={onKeyDown} style={{ gap: logical(gap) }}>
      {options.map((option, place) => {
        const colour = swatchColour(option.key);
        return (
          <button
            key={option.key}
            ref={(element) => {
              buttons.current[place] = element;
            }}
            type="button"
            role="radio"
            aria-checked={place === index}
            aria-label={lookSwatch(label, place + 1, options.length)}
            tabIndex={place === index ? 0 : -1}
            className={classes("ot-look__swatch", place === index && "is-selected", colour === null && "ot-look__swatch--plain")}
            data-look="swatch"
            data-key={option.key}
            onClick={() => onChoose(place)}
            style={{ width: logical(size), height: logical(size), backgroundColor: colour ?? undefined }}
          >
            {colour === null ? place + 1 : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One row: its name, the count beside a swatch row's name, and its control, a segmented choice for a style and
 * swatches for a colour, with the refusal's sentence under it when the refusal names it.
 *
 * @param props The `row`; the `offer` and the `look`, from which its options and its choice are read; whether its
 *   swatches are the `touch` size; the `refusal`, if any; and `onLook` with the look after a change.
 * @returns The row, or null for a row that is hidden.
 */
function LookRow({ row, offer, look, touch, refusal, onLook }: { readonly row: CreatorRow; readonly offer: CreatorOffer; readonly look: Appearance; readonly touch: boolean; readonly refusal: LookRefusal | null; readonly onLook: (look: Appearance) => void }): ReactElement | null {
  const view = creatorRow(offer, look, row);
  if (view.hidden) {
    return null;
  }
  const name = say(`look.rows.${row}`);
  const style = row === "hairStyle" || row === "clothesStyle";

  /**
   * Chooses an option of the row by its place.
   *
   * @param index The place.
   */
  const choose = (index: number): void => onLook(reduceCreator(offer, look, { kind: "choose", row, index }));

  return (
    <div className="ot-look__row" data-look="row" data-row={row} style={{ gap: logical(LOOK_PANEL.headGap) }}>
      <div className="ot-look__head">
        <span className="ot-look__name" aria-hidden="true">
          {name}
        </span>
        {style ? null : (
          <span className="ot-look__count" data-look="count">
            {lookCount(row, view.index + 1, view.options.length)}
          </span>
        )}
      </div>
      {style ? (
        <Segmented label={name} value={view.options[view.index]?.key ?? null} options={view.options.map((option) => ({ value: option.key, label: option.label }))} onChange={(key) => choose(view.options.findIndex((option) => option.key === key))} />
      ) : (
        <SwatchRow label={name} options={view.options} index={view.index} size={touch ? LOOK_PANEL.touchSwatch : LOOK_PANEL.swatch} gap={touch ? LOOK_PANEL.touchSwatchGap : LOOK_PANEL.swatchGap} onChoose={choose} />
      )}
      {refusal !== null && refusal.row === row ? (
        <p className="ot-look__note is-refused" data-look="refusal" role="alert">
          {refusal.sentence}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The Look panel.
 *
 * @param props See `LookPanelProps`.
 * @returns The window `frame` draws, holding the preview, the rows and the footer.
 * @throws Error when the look is not one the offer holds.
 * @example
 * <LookPanel
 *   frame={({ title, footer, children }) => <ModalWindow id="look" title={title} footer={footer} onClose={back} fit={lookWindowBox}>{children}</ModalWindow>}
 *   words={{ title: "Change look: Ardent", back: "Cancel", randomise: "Randomise", primary: "Confirm", saving: "Saving" }}
 *   rows={CREATOR_ROWS} offer={offer} look={look} onLook={setLook} source={source} saving={false} onBack={back} onPrimary={confirm}
 * />
 */
export function LookPanel({ frame, words, rows, offer, look, onLook, source, saving, primaryDisabled = false, note = null, after = null, refusal = null, touch = false, backGlyph, onBack, onPrimary, seed = pageSeed }: LookPanelProps): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const rowsCue = useScrollCue();
  const measured = useBodySize(host);
  const body = measured ?? LOOK_BODY_DEFAULT;
  const column = previewColumn(body, source === null ? null : previewStage(source.manifest.layout), touch);
  const shown = CREATOR_ROWS.filter((row) => rows.includes(row));
  const loose = refusal !== null && (refusal.row === null || !shown.includes(refusal.row) || creatorRow(offer, look, refusal.row).hidden);

  /**
   * Enter on a row's control is the primary action, where the browser would otherwise press the control again; a
   * primary action that is unavailable or already out does nothing, and the key reaches neither the control nor the
   * game. It is heard as the key goes down to the control, since the kit's own controls keep an activating key to
   * themselves.
   *
   * @param event The key press inside the rows.
   */
  const onRowKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter" || !(event.target instanceof Element) || event.target.getAttribute("role") !== "radio") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!saving && !primaryDisabled) {
      onPrimary();
    }
  };

  const footer = (
    <div className="ot-look__footer" data-look="footer" style={{ gap: logical(LOOK_PANEL.swatchGap * 2) }}>
      <Button {...(backGlyph === undefined ? {} : { icon: backGlyph })} onClick={onBack} disabled={saving}>
        {words.back}
      </Button>
      <Button icon="dice" onClick={() => onLook(reduceCreator(offer, look, { kind: "randomise", seed: seed() }))} disabled={saving}>
        {words.randomise}
      </Button>
      <span className="ot-look__primary" data-look="primary">
        <Button variant="primary" onClick={onPrimary} disabled={primaryDisabled && !saving} busy={saving} busyLabel={words.saving}>
          {words.primary}
        </Button>
      </span>
    </div>
  );

  return frame({
    title: words.title,
    footer,
    children: (
      <div ref={host} className="ot-look" data-look="panel" data-measured={measured === null ? "false" : "true"} data-saving={saving ? "true" : "false"} data-skin={look.skin} data-hair={look.hair} data-clothes={look.clothes} style={{ gap: logical(LOOK_PANEL.gap), opacity: measured === null ? 0 : undefined }}>
        <div className="ot-look__preview" data-look="preview" style={{ width: logical(column.width) }}>
          <LookFigure source={source} look={look} column={column} touch={touch} />
        </div>
        <div ref={rowsCue} className="ot-look__rows ot-scroll" data-look="rows" onKeyDownCapture={onRowKey} style={{ gap: logical(LOOK_PANEL.rowGap), paddingRight: logical(LOOK_PANEL.scrollGap) }}>
          {note === null ? null : (
            <p className="ot-look__note" data-look="note">
              {note}
            </p>
          )}
          {shown.map((row) => (
            <LookRow key={row} row={row} offer={offer} look={look} touch={touch} refusal={refusal} onLook={onLook} />
          ))}
          {after}
          {loose ? (
            <>
              <div className="ot-look__rule" style={{ height: logical(1), marginTop: logical(LOOK_PANEL.noteGap - LOOK_PANEL.rowGap), marginBottom: logical(LOOK_PANEL.noteGap - LOOK_PANEL.rowGap) }} />
              <p className="ot-look__note is-refused" data-look="refusal" role="alert">
                {refusal.sentence}
              </p>
            </>
          ) : null}
        </div>
      </div>
    ),
  });
}
